import { aabb2Overlaps } from "./aabb2";
import type { BRep, Face } from "./brep";
import { classifyContainmentFaceVec2 } from "./brep";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, ZERO_EPSILON } from "./core";
import type {
  CurveCurveIntersection,
  CurveCurveRangeIntersection,
  CurveCurvePointIntersection,
} from "./intersectLine2Line2";
import { intersectLine2Line2 } from "./intersectLine2Line2";
import type { Ray2Line2Intersection, Ray2PolyLine2Intersection } from "./intersectRay2";
import { IntersectionKind, intersectLine2Ray2, ray2Line2IntersectionRayParamCompare } from "./intersectRay2";
import { intersectRay3PlaneWithNormal } from "./intersectRay3";
import type { Line3Segment } from "./line3";
import type { Plane } from "./plane";
import { planeDistanceTo, planeEval, planeIsCoplanar, planeNormal, planeProject } from "./plane";
import { polyLine2AABB2, polyLine2SegmentsCollinearToLine } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import { ray2Eval, ray2New } from "./ray2";
import type { Ray3 } from "./ray3";
import { ray3DotProject, ray3Eval, ray3New } from "./ray3";
import type { Vec2 } from "./vec2";
import { vec2Dist, vec2DistSquared, vec2Dot, vec2Result, vec2Sub } from "./vec2";
import { vec3Cross, vec3Dist, vec3Dot, vec3IsInEdgeBounds, vec3Normalize, vec3Sub } from "./vec3";

type RaySegment = [number, number];

const { abs } = Math;

export interface FacePlaneIntersection {
  kind: IntersectionKind;
  segments: FacePlaneIntersectionSegment[];
}

/**
 * Intersects a plane with the z0 plane, returning a Ray3.
 * The returned ray's origin is the global 3D origin's projection onto the ray. The ray's direction
 * is normalized.
 * @param plane the plane to intersect with the z0 plane.
 * @returns Ray3 or null if the plane is parallel to the z0 plane.
 **/
export function intersectPlaneZ0(plane: Plane): Ray3 | null {
  const [nX, nY, nZ] = planeNormal(plane);
  const pX = plane.originX;
  const pY = plane.originY;
  const pZ = plane.originZ;
  let [dx, dy, dz] = vec3Cross(0, 0, 1, nX, nY, nZ);
  // parallel
  if (vec3Dot(dx, dy, dz, dx, dy, dz) < ZERO_EPSILON) {
    // TODO: check if coplanar
    return null;
  }

  [dx, dy, dz] = vec3Normalize(dx, dy, dz);

  const originProjectedToNormal = vec3Dot(pX, pY, pZ, nX, nY, nZ);
  const orthogonalProjectedToNormal = vec3Dot(dy, -dx, 0, nX, nY, nZ);
  if (abs(orthogonalProjectedToNormal) < ZERO_EPSILON) {
    // Should almost never happen, only if the plane is almost parallel to the z0 plane
    // which we already checked for.
    return null;
  }
  const distance = originProjectedToNormal / orthogonalProjectedToNormal;
  const rayResult = ray3New(distance * dy, -distance * dx, 0, dx, dy, dz);
  return rayResult;
}

export function intersectPlanePlane(plane0: Plane, plane1: Plane): Ray3 | null {
  const [plane0NormX, plane0NormY, plane0NormZ] = planeNormal(plane0);
  const [plane1NormX, plane1NormY, plane1NormZ] = planeNormal(plane1);

  let [dx, dy, dz] = vec3Cross(plane0NormX, plane0NormY, plane0NormZ, plane1NormX, plane1NormY, plane1NormZ);

  // parallel
  if (vec3Dot(dx, dy, dz, dx, dy, dz) < ZERO_EPSILON) {
    // TODO: check if coplanar
    return null;
  }

  // find the largest index of d
  let li = 0;
  let mi = abs(dx);
  const m1 = abs(dy);
  const m2 = abs(dz);

  if (m1 > mi) {
    li = 1;
    mi = m1;
  }

  if (m2 > mi) {
    li = 2;
    mi = m2;
  }

  let a1: number;
  let b1: number;
  let a2: number;
  let b2: number;

  if (li === 0) {
    a1 = plane0NormY;
    b1 = plane0NormZ;
    a2 = plane1NormY;
    b2 = plane1NormZ;
  } else if (li === 1) {
    a1 = plane0NormX;
    b1 = plane0NormZ;
    a2 = plane1NormX;
    b2 = plane1NormZ;
  } else {
    a1 = plane0NormX;
    b1 = plane0NormY;
    a2 = plane1NormX;
    b2 = plane1NormY;
  }

  // n dot X = d
  const d1 = -vec3Dot(plane0.originX, plane0.originY, plane0.originZ, plane0NormX, plane0NormY, plane0NormZ);
  const d2 = -vec3Dot(plane1.originX, plane1.originY, plane1.originZ, plane1NormX, plane1NormY, plane1NormZ);

  const den = a1 * b2 - b1 * a2;

  const x = (b1 * d2 - d1 * b2) / den;
  const y = (d1 * a2 - a1 * d2) / den;

  const rayResult = ray3New();

  if (li === 0) {
    rayResult.originX = 0;
    rayResult.originY = x;
    rayResult.originZ = y;
  } else if (li === 1) {
    rayResult.originX = x;
    rayResult.originY = 0;
    rayResult.originZ = y;
  } else {
    rayResult.originX = x;
    rayResult.originY = y;
    rayResult.originZ = 0;
  }

  [dx, dy, dz] = vec3Normalize(dx, dy, dz);

  rayResult.dirX = dx;
  rayResult.dirY = dy;
  rayResult.dirZ = dz;

  return rayResult;
}

export interface FaceFaceCoplanarIntersection {
  kind: IntersectionKind.TANGENT;
  face0: number;
  face1: number;
  // loops?
}

export interface FaceFaceTransverseIntersection {
  kind: IntersectionKind.TRANSVERSE;
  face0: number;
  face1: number;
  segments: FaceFaceIntersectionSegment[];
}

export type FaceFaceIntersection = FaceFaceTransverseIntersection | FaceFaceCoplanarIntersection;

export function intersectFaceFaceFromBReps(
  a: BRep,
  faceIndexA: number,
  b: BRep,
  faceIndexB: number,
  errs: string[],
): FaceFaceIntersection | null {
  const faceA = a.faces[faceIndexA];
  const faceB = b.faces[faceIndexB];

  const res = intersectFaceFace(faceA, faceB, errs);
  if (!res) {
    return null;
  }

  if (res.kind === IntersectionKind.TRANSVERSE) {
    res.face0 = faceIndexA;
    res.face1 = faceIndexB;
    for (const segment of res.segments) {
      segment.faceIndexA = faceIndexA;
      segment.faceIndexB = faceIndexB;
    }
  } else if (res.kind === IntersectionKind.TANGENT) {
    res.face0 = faceIndexA;
    res.face1 = faceIndexB;
  }

  return res;
}

export function intersectFaceFace(faceA: Face, faceB: Face, errs: string[]): FaceFaceIntersection | null {
  // coplanar
  // transverse
  // this algo is very similar to plane brep intersection, but we have to clip the plane intersection twice

  // dummy data
  const faceIndexA = -1;
  const faceIndexB = -1;

  const planeA = faceA.plane;
  const planeB = faceB.plane;

  if (planeIsCoplanar(planeA, planeB)) {
    // do 2d boolean
    errs.push("Coplanar faces not allowed yet");
    return null;
  }

  // the intersection ray in 3d
  const ray3 = intersectPlanePlane(planeA, planeB);
  if (!ray3) {
    return null;
  }

  const {
    originX: ray3OriginX,
    originY: ray3OriginY,
    originZ: ray3OriginZ,
    dirX: ray3DirX,
    dirY: ray3DirY,
    dirZ: ray3DirZ,
  } = ray3;

  function intersectRayInFace(faceIndex: number, { loops, plane }: Face) {
    // the intersection ray in 2d
    const { uX, uY, uZ, vX, vY, vZ } = plane;
    const [dx, dy, dz] = vec3Sub(ray3OriginX, ray3OriginY, ray3OriginZ, plane.originX, plane.originY, plane.originZ);

    const ray2 = ray2New(
      vec3Dot(dx, dy, dz, uX, uY, uZ),
      vec3Dot(dx, dy, dz, vX, vY, vZ),
      vec3Dot(ray3DirX, ray3DirY, ray3DirZ, uX, uY, uZ),
      vec3Dot(ray3DirX, ray3DirY, ray3DirZ, vX, vY, vZ),
    );

    const { originX: ray2OriginX, originY: ray2OriginY, dirX: ray2DirX, dirY: ray2DirY } = ray2;

    // intersect the ray with the polyline loops
    const rayplints = intersectRay2PolyLine2Array(loops, ray2OriginX, ray2OriginY, ray2DirX, ray2DirY);

    // tag the intersections with the origin face, we'll use this later
    for (const int of rayplints) {
      int.tag = faceIndex;
    }

    return rayplints;
  }

  const ray2IntsA = intersectRayInFace(0, faceA);
  const ray2IntsB = intersectRayInFace(1, faceB);

  if (ray2IntsA.length % 2 !== 0 || ray2IntsB.length % 2 !== 0) {
    errs.push("Unexpectedly found odd number of intersections");
    return null;
  }

  const ints = ray2IntsA.concat(ray2IntsB);

  ints.sort(ray2Line2IntersectionRayParamCompare);

  const segments: FaceFaceIntersectionSegment[] = [];

  let insideA = false;
  let insideB = false;

  // TODO handle coincident points in sorted intersection list

  for (let i = 0, l = ints.length; i < l; i++) {
    const int1 = ints[i];

    if (insideA && insideB) {
      const int0 = ints[i - 1];
      let loopA0 = -1;
      let loopA1 = -1;
      let loopB0 = -1;
      let loopB1 = -1;
      let edgeA0 = -1;
      let edgeA1 = -1;
      let edgeB0 = -1;
      let edgeB1 = -1;
      // TODO have an interface that has the loop, edge, faceU, faceV, edgeT arrays for A and B,
      // instead of so many nullable variables.
      let faceAU0: number | null = null;
      let faceAV0: number | null = null;
      let faceAU1: number | null = null;
      let faceAV1: number | null = null;
      let faceBU0: number | null = null;
      let faceBV0: number | null = null;
      let faceBU1: number | null = null;
      let faceBV1: number | null = null;
      let edgeTA0: number | null = null;
      let edgeTA1: number | null = null;
      let edgeTB0: number | null = null;
      let edgeTB1: number | null = null;
      let x0: number | null = null;
      let y0: number | null = null;
      let z0: number | null = null;
      let x1: number | null = null;
      let y1: number | null = null;
      let z1: number | null = null;

      if (int0.tag === 0) {
        loopA0 = int0.loopIndex;
        edgeA0 = int0.edgeIndex;
        edgeTA0 = int0.lineT;

        faceAU0 = int0.x;
        faceAV0 = int0.y;

        [x0, y0, z0] = planeEval(planeA, int0.x, int0.y);
        [faceBU0, faceBV0] = planeProject(planeB, x0, y0, z0);
      } else if (int0.tag === 1) {
        loopB0 = int0.loopIndex;
        edgeB0 = int0.edgeIndex;
        edgeTB0 = int0.lineT;

        [x0, y0, z0] = planeEval(planeB, int0.x, int0.y);
        [faceAU0, faceAV0] = planeProject(planeA, x0, y0, z0);

        faceBU0 = int0.x;
        faceBV0 = int0.y;
      }

      if (int1.tag === 0) {
        loopA1 = int1.loopIndex;
        edgeA1 = int1.edgeIndex;
        edgeTA1 = int1.lineT;

        faceAU1 = int1.x;
        faceAV1 = int1.y;

        [x1, y1, z1] = planeEval(planeA, int1.x, int1.y);
        [faceBU1, faceBV1] = planeProject(planeB, x1, y1, z1);
      } else if (int1.tag === 1) {
        loopB1 = int1.loopIndex;
        edgeB1 = int1.edgeIndex;
        edgeTB1 = int1.lineT;

        [x1, y1, z1] = planeEval(planeB, int1.x, int1.y);
        [faceAU1, faceAV1] = planeProject(planeA, x1, y1, z1);

        faceBU1 = int1.x;
        faceBV1 = int1.y;
      }

      if (faceAU0 === null || faceAV0 === null || faceAU1 === null || faceAV1 === null) {
        continue;
      }

      if (vec2DistSquared(faceAU0, faceAV0, faceAU1, faceAV1) < DIST_EPSILON_SQUARED) {
        if (int1.tag === 0) {
          insideA = !insideA;
        } else {
          insideB = !insideB;
        }

        continue;
      }

      if (
        faceBU0 === null ||
        faceBV0 === null ||
        faceBU1 === null ||
        faceBV1 === null ||
        loopA0 === null ||
        loopA1 === null ||
        loopB0 === null ||
        loopB1 === null ||
        edgeA0 === null ||
        edgeA1 === null ||
        edgeB0 === null ||
        edgeB1 === null ||
        x0 === null ||
        y0 === null ||
        z0 === null ||
        x1 === null ||
        y1 === null ||
        z1 === null
      ) {
        continue;
      }

      segments.push({
        faceIndexA,
        faceIndexB,

        x0,
        y0,
        z0,
        x1,
        y1,
        z1,

        faceAU0,
        faceAV0,
        faceAU1,
        faceAV1,

        faceBU0,
        faceBV0,
        faceBU1,
        faceBV1,

        loopA0,
        loopA1,
        loopB0,
        loopB1,

        edgeA0,
        edgeA1,
        edgeB0,
        edgeB1,

        edgeTA0,
        edgeTA1,
        edgeTB0,
        edgeTB1,
      });
    }

    if (int1.tag === 0) {
      insideA = !insideA;
    } else {
      insideB = !insideB;
    }
  }

  if (segments.length === 0) {
    return null;
  }

  return {
    kind: IntersectionKind.TRANSVERSE,
    face0: faceIndexA,
    face1: faceIndexB,
    segments,
  };
}

// Returns 3d lines representing segments common to both face outlines.
//
// This is mostly useful when we know that two faces are adjacent and their edges intersect somehow. Rather than guarantee
// intersection anywhere in the faces, we limit the search to its outline edges.
//
// `intersectFaceFace` can be used more generally for face-face intersection, but doesn't handle this situation robustly.
export function intersectFaceFaceAdjacent(faceA: Face, faceB: Face, segmentLengthThreshold = 0.1): Line3Segment[] {
  const planeA = faceA.plane;
  const planeB = faceB.plane;

  if (planeIsCoplanar(planeA, planeB)) {
    return [];
  }

  const ray3 = intersectPlanePlane(planeA, planeB);
  if (!ray3) {
    return [];
  }

  const {
    originX: ray3OriginX,
    originY: ray3OriginY,
    originZ: ray3OriginZ,
    dirX: ray3DirX,
    dirY: ray3DirY,
    dirZ: ray3DirZ,
  } = ray3;

  function outlineSortedSegmentsAlongRay(loops: PolyLine2[], plane: Plane): RaySegment[] {
    const [startU, startV] = planeProject(plane, ray3OriginX, ray3OriginY, ray3OriginZ);
    const [endU, endV] = planeProject(plane, ray3OriginX + ray3DirX, ray3OriginY + ray3DirY, ray3OriginZ + ray3DirZ);

    const planeSegments = polyLine2SegmentsCollinearToLine(loops[0], startU, startV, endU, endV);
    return planeSegments
      .map((segment) => {
        const [x0, y0, z0] = planeEval(plane, segment[0], segment[1]);
        const t0 = ray3DotProject(ray3OriginX, ray3OriginY, ray3OriginZ, ray3DirX, ray3DirY, ray3DirZ, x0, y0, z0);

        const [x1, y1, z1] = planeEval(plane, segment[2], segment[3]);
        const t1 = ray3DotProject(ray3OriginX, ray3OriginY, ray3OriginZ, ray3DirX, ray3DirY, ray3DirZ, x1, y1, z1);

        return [t0, t1].sort((a, b) => a - b) as RaySegment;
      })
      .sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));
  }

  const segmentsA = outlineSortedSegmentsAlongRay(faceA.loops, planeA);
  const segmentsB = outlineSortedSegmentsAlongRay(faceB.loops, planeB);

  const segments: Line3Segment[] = [];

  if (segmentsA.length === 0 || segmentsB.length === 0) {
    return [];
  } else {
    const raySegments = intersectRaySegments(segmentsA, segmentsB);
    raySegments.forEach((raySegment) => {
      const [x0, y0, z0] = ray3Eval(ray3OriginX, ray3OriginY, ray3OriginZ, ray3DirX, ray3DirY, ray3DirZ, raySegment[0]);
      const [x1, y1, z1] = ray3Eval(ray3OriginX, ray3OriginY, ray3OriginZ, ray3DirX, ray3DirY, ray3DirZ, raySegment[1]);
      if (vec3Dist(x0, y0, z0, x1, y1, z1) > segmentLengthThreshold) {
        segments.push([x0, y0, z0, x1, y1, z1]);
      }
    });
  }

  if (segments.length === 0) {
    return [];
  }

  return segments;
}

export interface FaceFaceIntersectionSegment {
  faceIndexA: number;
  faceIndexB: number;

  x0: number;
  y0: number;
  z0: number;
  x1: number;
  y1: number;
  z1: number;

  loopA0: number;
  loopA1: number; // -1 if the point is not on an edge
  loopB0: number;
  loopB1: number; // -1 if the point is not on an edge

  edgeA0: number;
  edgeA1: number;
  edgeB0: number;
  edgeB1: number;

  edgeTA0: number | null;
  edgeTA1: number | null;
  edgeTB0: number | null;
  edgeTB1: number | null;

  faceAU0: number;
  faceAV0: number;
  faceAU1: number;
  faceAV1: number;

  faceBU0: number;
  faceBV0: number;
  faceBU1: number;
  faceBV1: number;
}

export interface FacePlaneIntersectionSegment {
  planeU0: number;
  planeV0: number;

  planeU1: number;
  planeV1: number;

  faceU0: number;
  faceV0: number;

  faceU1: number;
  faceV1: number;

  loop0: number;
  loop1: number;

  loopEdgeIndex0: number;
  loopEdgeIndex1: number;
}

export function facePlaneIntersectionSegmentCopy(seg: FacePlaneIntersectionSegment): FacePlaneIntersectionSegment {
  return { ...seg };
}

export function intersectFacePlaneFromBRep(
  brep: BRep,
  faceIndex: number,
  cutPlane: Plane,
  errs: string[],
): FacePlaneIntersection | null {
  const face = brep.faces[faceIndex];
  return intersectFacePlane(face, cutPlane, errs);
}

export function intersectFacePlane(face: Face, cutPlane: Plane, errs: string[]): FacePlaneIntersection | null {
  if (planeIsCoplanar(face.plane, cutPlane)) {
    return { kind: IntersectionKind.TANGENT, segments: [] };
  }

  // the intersection ray in 3d
  const ray3 = intersectPlanePlane(face.plane, cutPlane);
  if (!ray3) {
    return null;
  }

  const {
    originX: ray3OriginX,
    originY: ray3OriginY,
    originZ: ray3OriginZ,
    dirX: ray3DirX,
    dirY: ray3DirY,
    dirZ: ray3DirZ,
  } = ray3;

  // the intersection ray in 2d
  const facePlane = face.plane;
  const { uX, uY, uZ, vX, vY, vZ } = facePlane;
  const [dx, dy, dz] = vec3Sub(
    ray3OriginX,
    ray3OriginY,
    ray3OriginZ,
    facePlane.originX,
    facePlane.originY,
    facePlane.originZ,
  );

  const ray2 = ray2New(
    vec3Dot(dx, dy, dz, uX, uY, uZ),
    vec3Dot(dx, dy, dz, vX, vY, vZ),
    vec3Dot(ray3DirX, ray3DirY, ray3DirZ, uX, uY, uZ),
    vec3Dot(ray3DirX, ray3DirY, ray3DirZ, vX, vY, vZ),
  );

  const { originX: ray2OriginX, originY: ray2OriginY, dirX: ray2DirX, dirY: ray2DirY } = ray2;

  // intersect the ray with the polyline loops, splitting it into pieces
  // filter the pieces for whether they are inside or outside of the loop

  const ray2Ints = intersectRay2PolyLine2Array(face.loops, ray2OriginX, ray2OriginY, ray2DirX, ray2DirY);

  if (ray2Ints.length % 2 !== 0) {
    errs.push("Unexpectedly found odd number of intersections when intersecting ray with loop collection");
    return null;
  }

  const segments: FacePlaneIntersectionSegment[] = [];

  for (let i = 0, l = ray2Ints.length; i < l; i += 2) {
    const int0 = ray2Ints[i];
    const int1 = ray2Ints[i + 1];

    // express intersection points in 3d
    const [gx0, gy0, gz0] = planeEval(facePlane, int0.x, int0.y);
    const [gx1, gy1, gz1] = planeEval(facePlane, int1.x, int1.y);

    const [planeU0, planeV0] = planeProject(cutPlane, gx0, gy0, gz0);
    const [planeU1, planeV1] = planeProject(cutPlane, gx1, gy1, gz1);

    if (vec2DistSquared(planeU0, planeV0, planeU1, planeV1) < DIST_EPSILON_SQUARED) {
      continue;
    }

    segments.push({
      planeU0,
      planeV0,
      planeU1,
      planeV1,

      faceU0: int0.x,
      faceV0: int0.y,
      faceU1: int1.x,
      faceV1: int1.y,
      loop0: int0.loopIndex,
      loop1: int1.loopIndex,
      loopEdgeIndex0: int0.edgeIndex,
      loopEdgeIndex1: int1.edgeIndex,
    });
  }

  return {
    kind: IntersectionKind.TRANSVERSE,
    segments,
  };
}

export interface Ray2Ray2Intersection {
  kind: IntersectionKind;
  tA: number;
  tB: number;

  x: number;
  y: number;
}

export function intersectRay2Ray2(
  originAX: number,
  originAY: number,
  dirAX: number,
  dirAY: number,
  originBX: number,
  originBY: number,
  dirBX: number,
  dirBY: number,
): Ray2Ray2Intersection | null {
  // http://mathworld.wolfram.com/Line-LineIntersection.html

  const x1 = originAX;
  const y1 = originAY;

  const x2 = originAX + dirAX;
  const y2 = originAY + dirAY;

  const x3 = originBX;
  const y3 = originBY;

  const x4 = originBX + dirBX;
  const y4 = originBY + dirBY;

  const ax = x1 - x2;
  const ay = y1 - y2;

  const bx = x3 - x4;
  const by = y3 - y4;

  const denom = ax * by - ay * bx;

  // parallel case
  if (abs(denom) < ZERO_EPSILON) {
    // check if line is on ray by projecting the first point onto the ray
    // and checking the squared distance

    const [pdx, pdy] = vec2Sub(x1, y1, originBX, originBY);
    const pt = vec2Dot(pdx, pdy, dirBX, dirBY);
    const [projx, projy] = ray2Eval(originBX, originBY, dirBX, dirBY, pt);

    if (vec2DistSquared(x1, y1, projx, projy) < DIST_EPSILON_SQUARED) {
      return {
        kind: IntersectionKind.TANGENT,
        tA: 0,
        tB: 0,
        x: x1,
        y: y1,
      };
    }

    // lines are parallel, not overlapping
    return null;
  }

  // crossing case
  const num0 = x1 * y2 - y1 * x2;
  const num1 = x3 * y4 - y3 * x4;
  const xnum = num0 * bx - ax * num1;
  const ynum = num0 * by - ay * num1;

  const x = xnum / denom;
  const y = ynum / denom;

  const kind = IntersectionKind.TRANSVERSE;

  return { kind, tA: vec2Dist(x, y, originAX, originAY), tB: vec2Dist(x, y, originBX, originBY), x, y };
}

export interface Segment3PlaneIntersection {
  kind: IntersectionKind;
  segmentIndex: number;
  segment: [number, number, number, number, number, number];
  intersection: Line3PlaneIntersection;
}

export function intersectPolyLine3Plane(
  plane: Plane,
  points: PolyLine3,
  tolerance: number = ZERO_EPSILON,
): Segment3PlaneIntersection[] {
  const ints: Segment3PlaneIntersection[] = [];

  if (points.length < 6) {
    return ints;
  }

  for (let i = 0, l = points.length - 3; i < l; i += 3) {
    const x1 = points[i];
    const y1 = points[i + 1];
    const z1 = points[i + 2];
    const x2 = points[i + 3];
    const y2 = points[i + 4];
    const z2 = points[i + 5];

    const res = intersectLine3Plane(plane, x1, y1, z1, x2, y2, z2, tolerance);

    if (res) {
      ints.push({
        kind: res.kind,
        segmentIndex: i,
        segment: [x1, y1, z1, x2, y2, z2],
        intersection: res,
      });
    }
  }

  return ints;
}

export interface Line3PlaneIntersection {
  kind: IntersectionKind;
  x: number;
  y: number;
  z: number;
}

export function intersectLine3Plane(
  plane: Plane,
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  tolerance: number = ZERO_EPSILON,
): Line3PlaneIntersection | null {
  const [sx, sy, sz] = vec3Sub(x0, y0, z0, x1, y1, z1);
  const [dx, dy, dz] = vec3Normalize(sx, sy, sz);

  const { originX, originY, originZ } = plane;

  const [px, py, pz] = vec3Normalize(...planeNormal(plane));

  const pnormDotLine = vec3Dot(px, py, pz, dx, dy, dz);

  // check if lines are parallel
  if (abs(pnormDotLine) < tolerance) {
    // check distance from line to plane
    if (planeDistanceTo(plane, x0, y0, z0) < tolerance) {
      return {
        kind: IntersectionKind.TANGENT,
        x: x0,
        y: y0,
        z: z0,
      };
    }
    // null case means no intersection
    return null;
  }

  const [wx, wy, wz] = vec3Sub(x0, y0, z0, originX, originY, originZ);

  const si = -vec3Dot(px, py, pz, wx, wy, wz) / pnormDotLine;

  const x = wx + si * dx + originX;
  const y = wy + si * dy + originY;
  const z = wz + si * dz + originZ;

  if (!vec3IsInEdgeBounds(x, y, z, x0, y0, z0, x1, y1, z1, tolerance)) {
    return null;
  }

  return { kind: IntersectionKind.TRANSVERSE, x, y, z };
}

export interface Ray2PolyLine2ArrayIntersection extends Ray2PolyLine2Intersection {
  u: number;
  v: number;
  loopIndex: number;
  tag: any; // whatever you want
}

//This variant of function intersectRay2PolyLine2 returns filtered intersections
//in the direction of the ray, unlike the original function which returns
//intersection on the ray in both the directions.
//It also uses another variant of intersectLine2Ray2V2 which is using
//a different function variant to ascertain if a point is between two other points.
//Also, this may be itty bitty bit faster than the original function if a filter is required later.
export function intersectRay2PolyLine2Filtered(
  pl: PolyLine2,
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
  tol: number = ZERO_EPSILON,
): Ray2PolyLine2Intersection[] {
  const ints: Ray2PolyLine2Intersection[] = [];

  for (let j = 0, lj = pl.length - 2; j < lj; j += 2) {
    const x0 = pl[j];
    const y0 = pl[j + 1];
    const x1 = pl[j + 2];
    const y1 = pl[j + 3];

    const res = intersectLine2Ray2(
      x0,
      y0,
      x1,
      y1,
      originX,
      originY,
      dirX,
      dirY,
      tol,
      true,
    ) as Ray2PolyLine2Intersection;

    if (res) {
      const dx = res.x - originX;
      const dy = res.y - originY;

      const t = vec2Dot(dx, dy, dirX, dirY);

      if (t < -tol) {
        continue;
      }

      res.edgeIndex = j / 2;
      ints.push(res);
    }
  }

  if (ints.length < 2) {
    return ints;
  }

  // sort intersections

  ints.sort(ray2Line2IntersectionRayParamCompare);

  return ints;
}

// intersectRay2PolyLine2Array returns a list of intersection points sorted by parameter along the ray.
// It is the responsbility of the caller to filter duplicated points.
export function intersectRay2PolyLine2Array(
  pls: PolyLine2[],
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
): Ray2PolyLine2ArrayIntersection[] {
  const ints: Ray2PolyLine2ArrayIntersection[] = [];

  for (let i = 0, l = pls.length; i < l; i++) {
    const pl = pls[i];

    for (let j = 0, lj = pl.length - 2; j < lj; j += 2) {
      const x0 = pl[j];
      const y0 = pl[j + 1];
      const x1 = pl[j + 2];
      const y1 = pl[j + 3];

      // do line intersection
      const res = intersectLine2Ray2(
        x0,
        y0,
        x1,
        y1,
        originX,
        originY,
        dirX,
        dirY,
        undefined,
        true,
      ) as Ray2PolyLine2ArrayIntersection;

      if (res) {
        res.loopIndex = i;
        res.edgeIndex = j / 2;
        ints.push(res);
      }
    }
  }

  if (ints.length < 2) {
    return ints;
  }

  // sort intersections

  ints.sort(ray2Line2IntersectionRayParamCompare);

  return ints;
}

// ray2Line2IntersectionMakeUnique combines intersection points that are within the given param tolerance. It mutates the input array.
export function ray2PolyLine2IntersectionMakeUnique(ints: Ray2Line2Intersection[], paramTol: number = DIST_EPSILON) {
  if (ints.length < 2) {
    return;
  }
  let cursor = 1;
  for (let i = 1; i < ints.length; i++) {
    const int0 = ints[i - 1];
    const int1 = ints[i];

    ints[cursor] = int1;

    if (abs(int0.t - int1.t) > paramTol) {
      cursor++;
    }
  }

  ints.length = cursor;
}

export function intersectPlaneRay(
  plane: Plane,
  rayOriginX: number,
  rayOriginY: number,
  rayOriginZ: number,
  rayDirX: number,
  rayDirY: number,
  rayDirZ: number,
): Vec2 | null {
  const { originX, originY, originZ } = plane;
  const [normX, normY, normZ] = planeNormal(plane);

  // intersect plane
  const res = intersectRay3PlaneWithNormal(
    rayOriginX,
    rayOriginY,
    rayOriginZ,
    rayDirX,
    rayDirY,
    rayDirZ,
    originX,
    originY,
    originZ,
    normX,
    normY,
    normZ,
  );

  // no intersection
  if (res === null) {
    return null;
  }

  // pt in global coords
  const [igx, igy, igz] = res;

  // pt in relative coords to plane origin
  const [ix, iy, iz] = vec3Sub(igx, igy, igz, originX, originY, originZ);

  // pt in plane coords
  vec2Result[0] = vec3Dot(ix, iy, iz, plane.uX, plane.uY, plane.uZ);
  vec2Result[1] = vec3Dot(ix, iy, iz, plane.vX, plane.vY, plane.vZ);

  // return uv
  return vec2Result;
}

export function intersectFaceRay(
  face: Face,
  rayOriginX: number,
  rayOriginY: number,
  rayOriginZ: number,
  rayDirX: number,
  rayDirY: number,
  rayDirZ: number,
): Vec2 | null {
  const plane = face.plane;

  const { originX, originY, originZ } = plane;
  const [normX, normY, normZ] = planeNormal(plane);

  // intersect plane
  const res = intersectRay3PlaneWithNormal(
    rayOriginX,
    rayOriginY,
    rayOriginZ,
    rayDirX,
    rayDirY,
    rayDirZ,
    originX,
    originY,
    originZ,
    normX,
    normY,
    normZ,
  );

  // no intersection
  if (res === null) {
    return null;
  }

  // pt in global coords
  const [igx, igy, igz] = res;

  // pt in relative coords to plane origin
  const [ix, iy, iz] = vec3Sub(igx, igy, igz, originX, originY, originZ);

  // pt in plane coords
  const iu = vec3Dot(ix, iy, iz, plane.uX, plane.uY, plane.uZ);
  const iv = vec3Dot(ix, iy, iz, plane.vX, plane.vY, plane.vZ);

  // check pt is in face
  if (!classifyContainmentFaceVec2(iu, iv, face)) {
    return null;
  }

  vec2Result[0] = iu;
  vec2Result[1] = iv;

  // return uv
  return vec2Result;
}

export function intersectLineSegment2Circle2(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  xC: number,
  yC: number,
  radius: number,
): Vec2[] {
  const v1x = x2 - x1;
  const v1y = y2 - y1;
  const v2x = x1 - xC;
  const v2y = y1 - yC;

  const b = -2 * (v1x * v2x + v1y * v2y);
  const c = 2 * (v1x * v1x + v1y * v1y);

  const d = Math.sqrt(b * b - 2 * c * (v2x * v2x + v2y * v2y - radius * radius));
  if (isNaN(d)) {
    return [];
  }

  const u1 = (b - d) / c;
  const u2 = (b + d) / c;

  const ret: Vec2[] = [];
  let p1: Vec2 = [];
  if (u1 <= 1 && u1 >= 0) {
    p1 = [x1 + v1x * u1, y1 + v1y * u1];
    ret.push(p1);
  }
  if (u2 <= 1 && u2 >= 0) {
    const p2 = [x1 + v1x * u2, y1 + v1y * u2];
    if (p1.length === 2) {
      if (vec2Dist(p1[0], p1[1], p2[0], p2[1]) < DIST_EPSILON) {
        return ret;
      }
    }
    ret.push(p2);
  }

  return ret;
}

export function polyLine2PolyLine2DoIntersect(a: PolyLine2, b: PolyLine2): boolean {
  for (let i = 0, l = a.length - 2; i < l; i += 2) {
    const xa0 = a[i];
    const ya0 = a[i + 1];
    const xa1 = a[i + 2];
    const ya1 = a[i + 3];

    if (vec2Dist(xa0, ya0, xa1, ya1) < 1e-3) {
      continue;
    }

    for (let j = 0, lj = b.length - 2; j < lj; j += 2) {
      const x0 = b[j];
      const y0 = b[j + 1];
      const x1 = b[j + 2];
      const y1 = b[j + 3];

      if (vec2Dist(x0, y0, x1, y1) < 1e-3) {
        continue;
      }

      const res = intersectLine2Line2(xa0, ya0, xa1, ya1, x0, y0, x1, y1);

      if (res) {
        return true;
      }
    }
  }

  return false;
}

export interface PolyLine2PolyLine2Intersection {
  edgeIndexA: number;
  edgeIndexB: number;
  int: CurveCurveIntersection;
}

export function intersectPolyLine2PolyLine2(
  a: PolyLine2,
  b: PolyLine2,
  epsilon: number = ZERO_EPSILON,
  dotEpsilon: number = 0,
  restrictToLineBounds = true,
): PolyLine2PolyLine2Intersection[] {
  const ints: PolyLine2PolyLine2Intersection[] = [];

  if (!aabb2Overlaps(polyLine2AABB2(a), polyLine2AABB2(b), epsilon)) {
    return ints;
  }

  for (let i = 0, l = a.length - 2; i < l; i += 2) {
    const xa0 = a[i];
    const ya0 = a[i + 1];
    const xa1 = a[i + 2];
    const ya1 = a[i + 3];

    // skip invalid shapes
    if (vec2Dist(xa0, ya0, xa1, ya1) < 1e-3) {
      continue;
    }

    for (let j = 0, lj = b.length - 2; j < lj; j += 2) {
      const x0 = b[j];
      const y0 = b[j + 1];
      const x1 = b[j + 2];
      const y1 = b[j + 3];

      // skip invalid shapes
      if (vec2Dist(x0, y0, x1, y1) < 1e-3) {
        continue;
      }

      const int = intersectLine2Line2(xa0, ya0, xa1, ya1, x0, y0, x1, y1, epsilon, dotEpsilon, restrictToLineBounds);
      if (int) {
        ints.push({
          edgeIndexA: i / 2,
          edgeIndexB: j / 2,
          int,
        });
      }
    }
  }

  return ints;
}

export function intersectPolyLine3PolyLine3IgnoreZ(a: PolyLine3, b: PolyLine3): PolyLine2PolyLine2Intersection[] {
  const ints: PolyLine2PolyLine2Intersection[] = [];

  for (let i = 0, l = a.length - 3; i < l; i += 3) {
    const xa0 = a[i];
    const ya0 = a[i + 1];
    const xa1 = a[i + 3];
    const ya1 = a[i + 4];

    if (vec2Dist(xa0, ya0, xa1, ya1) < 1e-3) {
      continue;
    }

    for (let j = 0, lj = b.length - 3; j < lj; j += 3) {
      const x0 = b[j];
      const y0 = b[j + 1];
      const x1 = b[j + 3];
      const y1 = b[j + 4];

      if (vec2Dist(x0, y0, x1, y1) < 1e-3) {
        continue;
      }

      const int = intersectLine2Line2(xa0, ya0, xa1, ya1, x0, y0, x1, y1);
      if (int) {
        ints.push({
          edgeIndexA: i / 3,
          edgeIndexB: j / 3,
          int,
        });
      }
    }
  }

  return ints;
}

/**
 * Sometimes there are polyline intersections very close to each other, for example when a segment overlaps another. Filter out those.
 * @param intersections Intersections to filter. The original array is not mutated.
 * @param tolerance Intersections within this distance from each other are filtered out.
 * @returns The filtered intersections.
 */
export function removeCoincidentPolyLine2Intersections(
  intersections: PolyLine2PolyLine2Intersection[],
  tolerance: number = DIST_EPSILON,
): PolyLine2PolyLine2Intersection[] {
  const toleranceSquared = tolerance * tolerance;
  function hasCoincidentIntersection(intersections: PolyLine2PolyLine2Intersection[], i: number): boolean {
    const intersection = intersections[i];
    if ((intersection.int as CurveCurveRangeIntersection).startx !== undefined) {
      // Never remove range intersections.
      return false;
    }
    const x = (intersection.int as CurveCurvePointIntersection).x;
    const y = (intersection.int as CurveCurvePointIntersection).y;
    for (let otherIndex = 0; otherIndex < intersections.length; ++otherIndex) {
      if (otherIndex === i) {
        continue;
      }
      const otherIntersection = intersections[otherIndex];
      if ((otherIntersection.int as CurveCurvePointIntersection).x !== undefined) {
        const otherX = (otherIntersection.int as CurveCurvePointIntersection).x;
        const otherY = (otherIntersection.int as CurveCurvePointIntersection).y;
        if (vec2DistSquared(x, y, otherX, otherY) < toleranceSquared) {
          return true;
        }
      } else {
        const otherX = (otherIntersection.int as CurveCurveRangeIntersection).endx;
        const otherY = (otherIntersection.int as CurveCurveRangeIntersection).endy;
        if (vec2DistSquared(x, y, otherX, otherY) < toleranceSquared) {
          return true;
        }
        const otherX2 = (otherIntersection.int as CurveCurveRangeIntersection).startx;
        const otherY2 = (otherIntersection.int as CurveCurveRangeIntersection).starty;
        if (vec2DistSquared(x, y, otherX2, otherY2) < toleranceSquared) {
          return true;
        }
      }
    }
    return false;
  }
  return intersections.filter((_, i) => !hasCoincidentIntersection(intersections, i));
}

function intersectRaySegments(segmentsA: RaySegment[], segmentsB: RaySegment[]): RaySegment[] {
  const result: RaySegment[] = [];

  // https://stackoverflow.com/a/49459224
  const lenA = segmentsA.length;
  let aIndex = 0;
  let aStart: number, aEnd: number;

  const lenB = segmentsB.length;
  let bIndex = 0;
  let bStart: number, bEnd: number;

  while (aIndex < lenA && bIndex < lenB) {
    aStart = segmentsA[aIndex][0];
    aEnd = segmentsA[aIndex][1];
    bStart = segmentsB[bIndex][0];
    bEnd = segmentsB[bIndex][1];
    if (aEnd < bStart) {
      // a ends before b
      aIndex++;
    } else if (bEnd < aStart) {
      // b ends before a
      bIndex++;
    } else {
      // a overlaps b
      result.push([aStart > bStart ? aStart : bStart, aEnd < bEnd ? aEnd : bEnd]);
      // the smaller range is considered processed
      if (aEnd < bEnd) {
        aIndex++;
      } else {
        bIndex++;
      }
    }
  }

  return result;
}
